import React, { useState } from 'react';
import { useTranslation } from '../../../i18n';

interface InspectPromptsModalProps {
  isOpen: boolean;
  onClose: () => void;
  simulationTitle?: string | null;
  evalData?: any;
}

export const InspectPromptsModal: React.FC<InspectPromptsModalProps> = ({
  isOpen,
  onClose,
  simulationTitle,
  evalData,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'system' | 'input' | 'rubric' | 'raw_response'>('input');
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const transcript = evalData?.transcript || [];
  const criteria = evalData?.criteria || evalData?.competencyScores || evalData?.rubrics || [];
  
  const scenarioInfo = {
    title: simulationTitle || evalData?.simulationTitle || evalData?.scenarioTitle || 'Customer Simulation',
    personaName: evalData?.personaName || 'Simulation Customer',
    personaRole: evalData?.personaRole || 'Customer',
    learnerRole: evalData?.roleName || evalData?.learnerRole || 'Representative',
    overallFeedbackPrompt: evalData?.overallFeedbackPrompt || evalData?.coaching?.overallFeedbackPrompt || undefined,
    scopeForImprovementPrompt: evalData?.scopeForImprovementPrompt || evalData?.coaching?.scopeForImprovementPrompt || undefined,
    passScore: evalData?.passScore || evalData?.passingScore || 70,
  };

  const inputPayloadToLLM = {
    purpose: 'SIMULATION_EVALUATION',
    scenario: scenarioInfo,
    learnerTranscriptTurns: (() => {
      let lastTime = 0;
      return transcript.map((t: any, idx: number) => {
        let timeMs = t.timestamp ? new Date(t.timestamp).getTime() : 0;
        if (!timeMs || isNaN(timeMs)) {
          timeMs = Date.now() - (transcript.length - idx) * 4000;
        }
        if (timeMs <= lastTime) {
          timeMs = lastTime + (t.speaker === 'AI' || t.speaker === 'AI_PERSONA' || t.role === 'assistant' ? 3500 : 2500);
        }
        lastTime = timeMs;
        return {
          turnIndex: typeof t.turnIndex === 'number' ? t.turnIndex : idx + 1,
          speaker: t.speaker || (t.role === 'assistant' ? 'AI' : 'Learner'),
          speakerName: t.speakerName || undefined,
          text: t.text || t.content || '',
          timestamp: new Date(timeMs).toISOString(),
        };
      });
    })(),
    rubricCriteria: criteria.map((c: any) => ({
      id: c.id || c.code || c.competencyCode,
      name: c.name || c.title || c.code,
      weight: c.weight,
      description: c.description,
      behavioralAnchors: c.behavioralAnchors || undefined,
    })),
    multimodalMetrics: {
      communication: evalData?.communicationMetrics || undefined,
      video: evalData?.videoEvidence || undefined,
    },
    coachingDirectives: {
      overallFeedbackPrompt: evalData?.overallFeedbackPrompt || evalData?.coaching?.overallFeedbackPrompt || null,
      scopeForImprovementPrompt: evalData?.scopeForImprovementPrompt || evalData?.coaching?.scopeForImprovementPrompt || null,
    },
  };

  const currentContent = (() => {
    switch (activeTab) {
      case 'system':
        return `You are an expert AI evaluator for relationship management and customer service simulations.
Your role is to assess the learner's performance against the scenario rubrics, grounded evidence in the transcript, and customer sentiment progression.

SCENARIO: ${scenarioInfo.title}
PERSONA: ${scenarioInfo.personaName} (${scenarioInfo.personaRole})
ROLE: ${scenarioInfo.learnerRole}

EVALUATION OBJECTIVES:
1. Score each criterion objectively based strictly on verbatim dialogue evidence from the transcript.
2. Flag any critical step violations (e.g. lack of mandatory disclosures, compliance requirements).
3. Compute voice delivery metrics, dialogue ratio, and speaking pace.
4. Output structured coaching recommendations with high-impact actionable alternatives.

SCENARIO DIRECTIVE (OVERALL FEEDBACK):
${scenarioInfo.overallFeedbackPrompt || 'Summarize learner performance across criteria based on observable behaviors.'}

SCENARIO DIRECTIVE (SCOPE FOR IMPROVEMENT):
${scenarioInfo.scopeForImprovementPrompt || 'Identify top actionable areas for growth.'}`;
      case 'input':
        return JSON.stringify(inputPayloadToLLM, null, 2);
      case 'rubric':
        return JSON.stringify(criteria, null, 2);
      case 'raw_response':
        return JSON.stringify(evalData || {}, null, 2);
      default:
        return '';
    }
  })();

  const handleCopy = () => {
    navigator.clipboard.writeText(currentContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" style={{ maxWidth: '820px', width: '92vw' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--line)', paddingBottom: '14px', marginBottom: '16px' }}>
          <div>
            <h3 style={{ fontFamily: 'var(--display)', fontSize: '18px', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>
              {t('scorecard.inspect_prompts_title', undefined, 'Inspect LLM Prompts & Evidence')}
            </h3>
            <p style={{ fontSize: '12.5px', color: 'var(--muted)', margin: '2px 0 0' }}>
              {scenarioInfo.title} · {t('scorecard.inspect_prompts_sub', undefined, 'Input Data & Scoring Payload Sent to LLM')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '18px', color: 'var(--muted)', cursor: 'pointer', padding: '4px 8px' }}
          >
            ✕
          </button>
        </div>

        {/* Navigation Tabs */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--line)', paddingBottom: '8px', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setActiveTab('input')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                background: activeTab === 'input' ? 'var(--blue)' : 'var(--surface)',
                color: activeTab === 'input' ? '#FFFFFF' : 'var(--navy)',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('scorecard.tab_input_data', { count: transcript.length }, `📥 Input Data to LLM (${transcript.length} turns)`)}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('system')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                background: activeTab === 'system' ? 'var(--blue)' : 'var(--surface)',
                color: activeTab === 'system' ? '#FFFFFF' : 'var(--navy)',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('scorecard.tab_system_instruction', undefined, 'System Instruction')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('rubric')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                background: activeTab === 'rubric' ? 'var(--blue)' : 'var(--surface)',
                color: activeTab === 'rubric' ? '#FFFFFF' : 'var(--navy)',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('scorecard.tab_evaluated_rubrics', { count: criteria.length }, `Evaluated Rubrics (${criteria.length})`)}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('raw_response')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                background: activeTab === 'raw_response' ? 'var(--blue)' : 'var(--surface)',
                color: activeTab === 'raw_response' ? '#FFFFFF' : 'var(--navy)',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('scorecard.tab_raw_response', undefined, 'Raw JSON Output')}
            </button>
          </div>

          <button
            type="button"
            onClick={handleCopy}
            style={{
              padding: '5px 10px',
              fontSize: '11.5px',
              fontWeight: 600,
              borderRadius: '6px',
              border: '1px solid var(--line)',
              background: '#FFFFFF',
              color: 'var(--navy)',
              cursor: 'pointer',
            }}
          >
            {copied ? t('scorecard.prompts_copied', undefined, '✓ Copied JSON') : t('scorecard.copy_prompts_json', undefined, '📋 Copy JSON')}
          </button>
        </div>

        {/* Content Box */}
        <div style={{ maxHeight: '55vh', overflowY: 'auto', background: '#0F172A', color: '#E2E8F0', padding: '16px', borderRadius: '8px', fontSize: '12px', fontFamily: 'monospace', lineHeight: 1.5 }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
            {currentContent}
          </pre>
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: '14px', marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
            {t('scorecard.eval_provider_label', undefined, 'Evaluation Provider:')} {evalData?.evaluationMetadata?.provider || evalData?.evaluatedBy || 'AI Evaluation Engine'} · {evalData?.evaluationMetadata?.model || 'gpt-4o / Qwen'}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="admin-btn admin-btn--secondary admin-btn--sm"
          >
            {t('scorecard.close_modal', undefined, 'Close Inspector')}
          </button>
        </div>
      </div>
    </div>
  );
};
